export type SystemLinkKind =
    'document' | 'dictionary' | 'report' | 'dashboard' | 'notification' | 'tasks' | 'internal' | 'external';

export interface ISystemLinkContext {
    baseUrl: string;
    basePath?: string;
    systemOrigins?: readonly string[];
}

export type SystemLinkLookup =
    | { kind: 'document' | 'notification'; id: string }
    | { kind: 'dictionary'; name: string }
    | { kind: 'report'; folder: string; name: string }
    | { kind: 'dashboard'; path: string[] };

export interface ISystemLink {
    href: string;
    kind: SystemLinkKind;
    label: string;
    title: string;
    detail: string;
    target: string;
    lookup?: SystemLinkLookup;
    isAddress: boolean;
}

const labels: Record<SystemLinkKind, string> = {
    document: 'Документ',
    dictionary: 'Справочник',
    report: 'Отчёт',
    dashboard: 'Дашборд',
    notification: 'Уведомление',
    tasks: 'Задачи',
    internal: 'Ссылка в системе',
    external: 'Внешний сайт',
};

const httpUrl = (href: string, baseUrl: string): URL | null => {
    try {
        const url = new URL(href, baseUrl);
        return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url : null;
    } catch {
        return null;
    }
};

const schemePattern = /^[a-z][a-z\d+.-]*:/i;
const bareHostPattern =
    /^(?:(?:[\p{L}\d](?:[\p{L}\d-]*[\p{L}\d])?\.)+\p{L}{2,}|(?:\d{1,3}\.){3}\d{1,3})(?::\d+)?(?:[/?#]\S*)?$/u;

const appRoutePattern = /^(?:document|notification|dashboards|dictionaries|reports|tasks|edo\/Document)(?=[/?#]|$)/i;

/**
 * В URL-колонку таблицы значение вводит пользователь: «www.site.ru» без схемы — это внешний
 * сайт, а не путь внутри системы, а произвольный текст или UNC-путь ссылкой не является.
 * Без этого относительный разбор превращал любое значение в «Ссылку в системе» на текущем хосте.
 */
const normalizeAddress = (href: string): string | null => {
    const value = href.trim();
    // Домен проверяется раньше схемы: «portal.ru:8443/app» иначе разобрался бы как схема «portal.ru:».
    if (bareHostPattern.test(value)) return `https://${value}`;
    // Хранимки и правила пишут внутренние адреса без ведущего слеша («document/428477»):
    // относительно страницы документа такой путь вёл бы на /document/document/428477.
    if (appRoutePattern.test(value)) return `/${value}`;
    return schemePattern.test(value) || value.startsWith('/') ? value : null;
};

/** Ключ документа или уведомления: без ведущих нулей и в пределах int, иначе пакет отклонит запрос. */
const entityId = (value: string): string | undefined => {
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 && id <= 2147483647 ? String(id) : undefined;
};

export const trimBasePath = (basePath?: string) => (basePath ?? '').replace(/\/+$/, '');

/** Хост в том виде, как его ввели: URL хранит кириллический домен в punycode («xn--…»). */
const readableHost = (href: string, url: URL): string => {
    const typed = href.trim().replace(/^[a-z][a-z\d+.-]*:\/\//i, '').match(/^[^/?#\s]+/)?.[0];
    try {
        return typed && new URL(`https://${typed}`).host === url.host ? typed.toLowerCase() : url.host;
    } catch {
        return url.host;
    }
};

export const resolveSystemLink = (
    href: string,
    text: string,
    context: ISystemLinkContext,
    target = '_blank',
): ISystemLink | null => {
    const address = normalizeAddress(href);
    if (!address) return null;
    const url = httpUrl(address, context.baseUrl);
    const base = httpUrl(context.baseUrl, context.baseUrl);
    if (!url || !base) return null;

    // Только явно настроенные origin: путь /document на чужом сайте не делает ссылку внутренней.
    const origins = new Set([base.origin]);
    context.systemOrigins?.forEach((origin) => {
        const parsed = httpUrl(origin, context.baseUrl);
        if (parsed && /^https?:\/\//i.test(origin)) origins.add(parsed.origin);
    });
    let lookup: SystemLinkLookup | undefined;
    let kind: SystemLinkKind = 'external';
    let detail = readableHost(href, url);
    if (origins.has(url.origin)) {
        kind = 'internal';
        detail = `${url.pathname}${url.search}${url.hash}`;
        const basePath = trimBasePath(context.basePath);
        let path = url.pathname.replace(/\/+$/, '');
        const legacyDocument = path.match(/^\/edo\/Document\/DocumentView\/(\d+)$/i);
        if (basePath && path.startsWith(`${basePath}/`)) path = path.slice(basePath.length);
        const documentId = entityId((path.match(/^\/document\/(\d+)$/) ?? legacyDocument)?.[1] ?? '');
        const notificationId = entityId(path.match(/^\/notification\/(\d+)$/)?.[1] ?? '');
        const dashboard = path.match(/^\/dashboards\/([^/]+)(?:\/[^/]+)?$/);
        if (documentId) {
            kind = 'document';
            lookup = { kind, id: documentId };
            detail = `ID ${documentId}`;
        } else if (path === '/dictionaries' && url.searchParams.get('dictName')) {
            kind = 'dictionary';
            lookup = { kind, name: url.searchParams.get('dictName')! };
            detail = url.searchParams.get('dictName')!;
        } else if (path === '/reports' && url.searchParams.get('folder') && url.searchParams.get('name')) {
            kind = 'report';
            lookup = { kind, folder: url.searchParams.get('folder')!, name: url.searchParams.get('name')! };
            detail = url.searchParams.get('name')!;
        } else if (dashboard) {
            kind = 'dashboard';
            try {
                lookup = { kind, path: path.slice('/dashboards/'.length).split('/').map(decodeURIComponent) };
            } catch {
                /* Некорректное кодирование: сама ссылка остаётся доступной. */
            }
            detail = path.slice('/dashboards/'.length);
        } else if (notificationId) {
            kind = 'notification';
            lookup = { kind, id: notificationId };
            detail = `ID ${notificationId}`;
        } else if (path === '/tasks') {
            kind = 'tasks';
            detail = 'Раздел системы';
        }
    }

    const caption = text.trim();
    const captionUrl = httpUrl(caption, context.baseUrl);
    const bareCaptionUrl = httpUrl(`http://${caption}`, context.baseUrl);
    const isAddress =
        !caption ||
        caption === href.trim() ||
        captionUrl?.href === url.href ||
        (bareCaptionUrl?.host === url.host &&
            bareCaptionUrl?.pathname === url.pathname &&
            bareCaptionUrl?.search === url.search &&
            bareCaptionUrl?.hash === url.hash);
    return {
        href: url.href,
        lookup: isAddress ? lookup : undefined,
        isAddress,
        kind,
        label: labels[kind],
        title: isAddress ? (kind === 'external' ? detail : labels[kind]) : caption,
        detail,
        target: kind === 'external' ? '_blank' : target || '_blank',
    };
};
